// src/components/RelatedPostsSlider.jsx
import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay } from 'swiper/modules';
import { normalizeMediaUrl } from '../api/base';

// Import Swiper styles
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

const Section = styled.section`
  background: var(--bg);
  padding: 48px 0;
  border-top: 1px solid var(--bdr);
  
  @media (max-width: 768px) {
    padding: 32px 0;
  }
`;

const Container = styled.div`
  width: min(1400px, 96vw);
  margin: 0 auto;
  padding: 0 20px;
  
  @media (max-width: 768px) {
    padding: 0 16px;
  }
`;

const Title = styled.h2`
  margin: 0 0 32px;
  font-size: 28px;
  font-weight: 700;
  color: var(--fg);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  
  @media (max-width: 768px) {
    font-size: 24px;
    margin-bottom: 24px;
  }
`;

const SliderWrapper = styled.div`
  position: relative;
  padding: 0 60px; /* Добавляем отступы для кнопок */
  
  .swiper {
    padding-bottom: 40px;
    overflow: visible; /* Позволяем кнопкам выходить за границы */
  }
  
  .swiper-slide {
    height: auto;
  }
  
  .swiper-pagination {
    bottom: 0;
    
    .swiper-pagination-bullet {
      width: 12px;
      height: 12px;
      background: var(--muted);
      opacity: 0.3;
      transition: all 0.3s ease;
      
      &.swiper-pagination-bullet-active {
        background: var(--primary);
        opacity: 1;
        transform: scale(1.2);
      }
    }
  }
  
  .swiper-button-next,
  .swiper-button-prev {
    width: 48px;
    height: 48px;
    margin-top: -24px;
    background: var(--card);
    border: 2px solid var(--bdr);
    border-radius: 50%;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    transition: all 0.3s ease;
    z-index: 10;
    
    &:after {
      font-size: 18px;
      font-weight: 700;
      color: var(--fg);
    }
    
    &:hover {
      background: var(--primary);
      border-color: var(--primary);
      transform: scale(1.1);
      box-shadow: 0 6px 20px rgba(176, 141, 87, 0.3);
      
      &:after {
        color: var(--primaryFg);
      }
    }
    
    &.swiper-button-disabled {
      opacity: 0.3;
      cursor: not-allowed;
      
      &:hover {
        transform: none;
        background: var(--card);
        border-color: var(--bdr);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        
        &:after {
          color: var(--fg);
        }
      }
    }
  }
  
  .swiper-button-prev {
    left: 0;
  }
  
  .swiper-button-next {
    right: 0;
  }
  
  @media (max-width: 768px) {
    padding: 0 20px; /* Уменьшаем отступы на мобильном */
    
    .swiper-button-next,
    .swiper-button-prev {
      display: none;
    }
  }
  
  @media (max-width: 480px) {
    padding: 0 10px;
  }
`;

const PostCard = styled(Link)`
  display: block;
  background: var(--card);
  border-radius: 16px;
  overflow: hidden;
  text-decoration: none;
  color: inherit;
  transition: all 0.3s ease;
  box-shadow: var(--shadow);
  height: 100%;
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.15);
  }
`;

const PostImage = styled.div`
  aspect-ratio: 16 / 9;
  overflow: hidden;
  position: relative;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.3s ease;
  }
  
  ${PostCard}:hover & img {
    transform: scale(1.05);
  }
`;

const LogoOverlay = styled.div`
  position: absolute;
  top: 16px;
  left: 16px;
  background: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 8px 12px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  backdrop-filter: blur(4px);
`;

const ShareButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  width: 36px;
  height: 36px;
  background: rgba(255, 255, 255, 0.9);
  border: none;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  backdrop-filter: blur(4px);
  
  &:hover {
    background: var(--primary);
    color: var(--primaryFg);
    transform: scale(1.1);
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const PostContent = styled.div`
  padding: 20px;
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const PostTitle = styled.h3`
  margin: 0 0 12px;
  font-size: 18px;
  font-weight: 600;
  line-height: 1.4;
  color: var(--fg);
  
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const PostExcerpt = styled.p`
  margin: 0 0 16px;
  font-size: 14px;
  line-height: 1.5;
  color: var(--muted);
  flex: 1;
  
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const PostMeta = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 13px;
  color: var(--muted);
  margin-top: auto;
`;

const PostDate = styled.span`
  font-weight: 500;
`;

const ReadTime = styled.span`
  font-weight: 500;
`;

// Функция для расчета времени чтения
function calculateReadingTime(html) {
  if (!html) return 1;
  
  const text = html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
  const wordCount = text.split(' ').filter(word => word.length > 0).length;
  const readingTime = Math.ceil(wordCount / 200);
  
  return Math.max(1, readingTime);
}

// Функция для форматирования даты
function formatDate(dateString) {
  if (!dateString) return '';
  
  const date = new Date(dateString);
  return date.toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });
}

// Функция для создания текста для шаринга
function createShareText(post) {
  return `${post.title} - АСТОРИУС`;
}

// Функция для шаринга
function handleShare(post, e, basePath = "/blog") {
  e.preventDefault();
  e.stopPropagation();
  
  const shareData = {
    title: post.title,
    text: createShareText(post),
    url: `${window.location.origin}${basePath}/${post.slug}`
  };
  
  if (navigator.share && navigator.canShare && navigator.canShare(shareData)) {
    navigator.share(shareData).catch(console.error);
  } else {
    // Fallback - копируем ссылку в буфер обмена
    const url = shareData.url;
    navigator.clipboard.writeText(url).then(() => {
      // Можно показать уведомление об успешном копировании
      }).catch(console.error);
  }
}

export default function RelatedPostsSlider({ posts = [], currentSlug, maxPosts = 6, basePath = "/blog" }) {
  const { t } = useTranslation();
  
  // Фильтруем текущую статью и ограничиваем количество
  const filteredPosts = posts
    .filter(post => post.slug !== currentSlug)
    .slice(0, maxPosts);
  
  if (!filteredPosts.length) return null;
  
  return (
    <Section>
      <Container>
        <Title>{t('blogPost.readAlso', 'Читайте также')}</Title>
        
        <SliderWrapper>
          <Swiper
            modules={[Navigation, Pagination, Autoplay]}
            spaceBetween={24}
            slidesPerView={1}
            navigation
            pagination={{ clickable: true }}
            autoplay={{
              delay: 5000,
              disableOnInteraction: false,
              pauseOnMouseEnter: true
            }}
            breakpoints={{
              640: {
                slidesPerView: 2,
                spaceBetween: 20
              },
              1024: {
                slidesPerView: 2,
                spaceBetween: 24
              }
            }}
            loop={filteredPosts.length > 2}
          >
            {filteredPosts.map((post) => (
              <SwiperSlide key={post.id || post.slug}>
                <PostCard to={`${basePath}/${post.slug}`}>
                  {post.cover && (
                    <PostImage>
                      <img
                        src={normalizeMediaUrl(post.cover)}
                        alt={post.title}
                        loading="lazy"
                        decoding="async"
                      />
                      <LogoOverlay>ANWIN</LogoOverlay>
                      <ShareButton
                        onClick={(e) => handleShare(post, e, basePath)}
                        title="Поделиться"
                        aria-label="Поделиться статьей"
                      >
                        <svg viewBox="0 0 24 24" fill="currentColor">
                          <path d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92s2.92-1.31 2.92-2.92-1.31-2.92-2.92-2.92z"/>
                        </svg>
                      </ShareButton>
                    </PostImage>
                  )}
                  
                  <PostContent>
                    <PostTitle>{post.title}</PostTitle>
                    
                    {post.excerpt && (
                      <PostExcerpt>{post.excerpt}</PostExcerpt>
                    )}
                    
                    <PostMeta>
                      <PostDate>{formatDate(post.date || post.created_at)}</PostDate>
                      <ReadTime>
                        {calculateReadingTime(post.body_html || post.content || '')} мин. читать
                      </ReadTime>
                    </PostMeta>
                  </PostContent>
                </PostCard>
              </SwiperSlide>
            ))}
          </Swiper>
        </SliderWrapper>
      </Container>
    </Section>
  );
}